import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {useMapsLibrary} from '@vis.gl/react-google-maps';
import type {GoogleMapAutocompleteProps} from '@Components/google-map/components/google-map-autocomplete/google-map-autocomplete.types';
import {autocompleteSearchID} from '@Components/google-map/components/google-map-autocomplete/google-map-autocomplete.types';
import {InputField} from '@Components/input-field-v2';
import type {InputFieldControlItemData, InputFieldControlItemEvent, InputType} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {EventDropdown} from '@Components/event-wizard/event-dropdown';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItem3} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import styles from './google-map-autocomplete-content.module.scss';
import {extractPlaceDetailsFromAutocompletePlaceResult} from '@Components/google-map/google-map.helper';
import AutocompletePrediction = google.maps.places.AutocompletePrediction;

export function GoogleMapAutocompleteContent({onLocationChange, inputFieldSize = InputFieldSize.XSMALL}: GoogleMapAutocompleteProps): ReactElement<any> {
  const [placesAutoCompleteService, setPlacesAutoCompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [autocompletePredictions, setAutocompletePredictions] = useState<AutocompletePrediction[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isUserTyping, setIsUserTyping] = useState<boolean>(false);
  const [searchVal, setSearchVal] = useState<string>('');

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>(undefined);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places) {
      return;
    }
    setPlacesAutoCompleteService(new places.AutocompleteService());
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    placesServiceRef.current = new google.maps.places.PlacesService(document.createElement('div'));
  }, [places]);

  const regenerateSessionToken = (): void => {
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
  };

  const onInputChangeEnded = async (value: string): Promise<void> => {
    setIsUserTyping(false);

    if (!value) {
      setAutocompletePredictions([]);
      setIsDropdownOpen(false);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);

    await placesAutoCompleteService?.getPlacePredictions(
      {
        input: value,
        sessionToken: sessionTokenRef.current,
      },
      (a) => {
        setAutocompletePredictions(a ?? []);
        setIsDropdownOpen(!!a && a.length > 0);
        setIsSearching(false);
      }
    );
  };

  const onInputChangeReactComponent = (value: InputType): void => {
    if (isSearching) {
      return;
    }
    setSearchVal(value.toString());
    setIsUserTyping(true);
  };

  const onSelectPlaceFromOptions = (autocompletePrediction: AutocompletePrediction): void => {
    setIsSearching(true);

    placesServiceRef.current?.getDetails(
      {
        placeId: autocompletePrediction.place_id,
        sessionToken: sessionTokenRef.current,
        fields: ['geometry', 'address_components', 'formatted_address'],
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          if (onLocationChange) {
            onLocationChange(extractPlaceDetailsFromAutocompletePlaceResult(autocompletePrediction, place));
          }
          setIsSearching(false);
          regenerateSessionToken();
        }
      }
    );
  };

  const getSelector = (): ReactElement<any> => {
    return (
      <InputField
        placeholder={window.i18next.t('pmwjs_search_location')}
        onInputChange={onInputChangeReactComponent}
        onInputChangeEnded={(val: string) => {
          void onInputChangeEnded(val);
        }}
        size={inputFieldSize}
        input={searchVal}
        controls={{
          showDefaultControls: false,
          showSingleDefaultControlOnly: false,
          onClick(iconName: string, e: InputFieldControlItemEvent) {
            e.stopPropagation();

            if (iconName === 'icon-search') {
              void onInputChangeEnded(searchVal ?? '');
            } else if (iconName === 'icon-cut') {
              void onInputChangeEnded('');
              setSearchVal('');
            }
          },
          customControls: [getAutocompleteControls()],
        }}
      />
    );
  };

  const getAutocompleteControls = (): InputFieldControlItemData => {
    if (isSearching) {
      return {
        id: 'searching',
        type: InputControlItemType.INACTIVE_ICON,
        icon: '',
        isLoading: true,
      };
    }

    if (isUserTyping || searchVal === '') {
      return {
        id: 'icon-search',
        type: InputControlItemType.ICON_BTNS,
        icon: 'icon-search',
        isBorderLess: true,
      };
    }

    return {
      id: 'icon-cut',
      type: InputControlItemType.ICON_BTNS,
      icon: 'icon-close',
      isBorderLess: true,
    };
  };

  const getContentItems = (): ControlledListItem3[] => {
    if (!autocompletePredictions) {
      setIsDropdownOpen(false);
      return [];
    }

    return autocompletePredictions.map((autocompletePrediction) => {
      return {
        id: autocompletePrediction.place_id,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: autocompletePrediction.structured_formatting.main_text,
        textClass: 'body-md-bold',
        subText: autocompletePrediction.structured_formatting.secondary_text,
        subtextClass: '-truncated-text _full-width',
        className: `${styles.listItem} _overflow-hidden`,
        onClick: (): void => {
          onSelectPlaceFromOptions(autocompletePrediction);
        },
      };
    });
  };

  return (
    <EventDropdown
      selector={getSelector()}
      className="_full-width"
      popup={<ControlledList id={autocompleteSearchID} items={getContentItems()} />}
      popupClass={`${styles.popup} _full-width`}
      alignment={ALIGNMENT_TYPE.BOTTOM_START}
      open={isDropdownOpen}
      doCloseOnInnerClick
      onOpen={() => {
        setIsDropdownOpen(true);
      }}
      onClose={() => {
        setIsDropdownOpen(false);
      }}
    />
  );
}
